import { useState } from "react";
import { Mail } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function EmailCodeForm({ onSuccess, idPrefix = "signin", initialEmail = "" }) {
  const { requestCode, verifyCode } = useAuth();
  const [stage, setStage] = useState("email"); // email | code
  const [email, setEmail] = useState(initialEmail);
  const [code, setCode] = useState(["", "", "", "", "", ""]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submitEmail = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    const res = await requestCode(email);
    setLoading(false);
    if (res.ok) {
      setStage("code");
      toast.success("We've emailed you a 6-digit code and a sign-in link.");
    } else {
      setError(res.error);
    }
  };

  const onCodeChange = (idx, val) => {
    if (val.length > 1) {
      const digits = val.replace(/\D/g, "").slice(0, 6 - idx).split("");
      if (!digits.length) return;
      const next = [...code];
      digits.forEach((d, k) => { next[idx + k] = d; });
      setCode(next);
      document.getElementById(`${idPrefix}-code-${Math.min(idx + digits.length, 5)}`)?.focus();
      return;
    }
    if (!/^\d?$/.test(val)) return;
    const next = [...code];
    next[idx] = val;
    setCode(next);
    if (val && idx < 5) document.getElementById(`${idPrefix}-code-${idx + 1}`)?.focus();
  };

  const onCodePaste = (idx) => (e) => {
    e.preventDefault();
    const digits = (e.clipboardData.getData("text") || "").replace(/\D/g, "").slice(0, 6 - idx).split("");
    if (!digits.length) return;
    const next = [...code];
    digits.forEach((d, k) => { next[idx + k] = d; });
    setCode(next);
    document.getElementById(`${idPrefix}-code-${Math.min(idx + digits.length, 5)}`)?.focus();
  };

  const onCodeKeyDown = (idx) => (e) => {
    if (e.key === "Backspace" && !code[idx] && idx > 0) {
      document.getElementById(`${idPrefix}-code-${idx - 1}`)?.focus();
    }
  };

  const submitCode = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    const res = await verifyCode(email, code.join(""));
    setLoading(false);
    if (res.ok) {
      onSuccess?.();
    } else {
      setError(res.error);
    }
  };

  if (stage === "email") {
    return (
      <form onSubmit={submitEmail} data-testid={`${idPrefix}-email-form`}>
        <label className="label-mono">Email address</label>
        <input
          type="email"
          inputMode="email"
          autoComplete="email"
          autoCapitalize="none"
          spellCheck={false}
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@research.org"
          className="mt-2 w-full min-h-[44px] rounded-md border border-line px-4 py-3 text-sm outline-none focus:border-brandblue focus:ring-2 focus:ring-brandblue/20"
          data-testid={`${idPrefix}-email-input`}
        />
        {error && <p className="mt-2 text-xs text-red-500" data-testid={`${idPrefix}-error`}>{error}</p>}
        <button
          type="submit"
          disabled={loading}
          className="btn-pill mt-4 w-full bg-navy py-3 text-sm font-semibold text-white hover:bg-navydark disabled:opacity-60"
          data-testid={`${idPrefix}-submit-email`}
        >
          {loading ? "Sending..." : "Send verification code →"}
        </button>
      </form>
    );
  }

  return (
    <form onSubmit={submitCode} data-testid={`${idPrefix}-code-form`}>
      <div className="flex items-center gap-2 text-sm text-subtle">
        <Mail className="h-4 w-4 text-green-600" strokeWidth={1.5} />
        <span>
          Code sent to <span className="font-semibold text-ink">{email}</span>.{" "}
          <button
            type="button"
            onClick={() => { setStage("email"); setCode(["", "", "", "", "", ""]); setError(""); }}
            className="-m-1 rounded p-1 text-brandblue underline"
            data-testid={`${idPrefix}-change-email`}
          >
            Change
          </button>
        </span>
      </div>
      <div className="mt-4 flex justify-between gap-2">
        {code.map((c, i) => (
          <input
            key={i}
            id={`${idPrefix}-code-${i}`}
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            autoComplete={i === 0 ? "one-time-code" : "off"}
            value={c}
            onChange={(e) => onCodeChange(i, e.target.value)}
            onPaste={onCodePaste(i)}
            onKeyDown={onCodeKeyDown(i)}
            className="h-12 w-full rounded-md border border-line text-center font-heading text-lg font-bold outline-none focus:border-brandblue focus:ring-2 focus:ring-brandblue/20"
            data-testid={`${idPrefix}-code-input-${i}`}
          />
        ))}
      </div>
      {error && <p className="mt-3 text-xs text-red-500" data-testid={`${idPrefix}-error`}>{error}</p>}
      <button
        type="submit"
        disabled={loading}
        className="btn-pill mt-4 w-full bg-navy py-3 text-sm font-semibold text-white hover:bg-navydark disabled:opacity-60"
        data-testid={`${idPrefix}-verify-code`}
      >
        {loading ? "Verifying..." : "Verify code →"}
      </button>
      <p className="mt-3 text-center font-mono text-[10px] leading-relaxed tracking-[0.1em] text-subtle">
        CODES EXPIRE IN 15 MINUTES · OR CLICK THE LINK IN THE EMAIL
      </p>
    </form>
  );
}
